import { create } from "zustand";

export type FilterValue = {
  id: string; // backend ID / value to send
  value: string; // human-readable label (for display)
  label?: string; // optional explicit label, mirrors API 'label' when available
};

// Stable empty references used as selector fallbacks.
// React's useSyncExternalStore (used internally by Zustand) requires the
// getSnapshot result to be the same reference when nothing changed.
// Using `?? {}` / `?? []` inline would create a *new* object/array on every
// selector call, making Object.is() return false every time and triggering an
// infinite re-render loop ("getSnapshot should be cached" / "Maximum update
// depth exceeded").
const EMPTY_VIEW_FILTERS: Record<string, FilterValue[]> = {};
const EMPTY_FILTER_VALUES: FilterValue[] = [];

type TTableFilterStore = {
  /** viewName → filterKey → FilterValue[] */
  filters: Record<string, Record<string, FilterValue[]>>;
  addFilter: (viewName: string, filterKey: string, filterValue: FilterValue) => void;
  removeFilter: (viewName: string, filterKey: string, id: string) => void;
  /**
   * When filterKey is omitted, resets ALL filterKeys for the given viewName.
   * When filterKey is provided, resets only that key within the viewName.
   */
  resetFilters: (viewName: string, filterKey?: string) => void;
  /** Clears ALL filters across every viewName — call on logout / session expiry. */
  resetAllFilters: () => void;
  getValues: (viewName: string, filterKey: string) => FilterValue[];
};

export const useTableFilterBaseStore = create<TTableFilterStore>((set, get) => ({
  filters: {},

  addFilter: (viewName, filterKey, filterValue) =>
    set((state) => {
      const viewFilters = state.filters[viewName] ?? {};
      const existingFilters = viewFilters[filterKey] || [];
      // Check if this ID already exists
      if (existingFilters.some((f) => f.id === filterValue.id)) return state;

      return {
        filters: {
          ...state.filters,
          [viewName]: {
            ...viewFilters,
            [filterKey]: [...existingFilters, filterValue],
          },
        },
      };
    }),

  removeFilter: (viewName, filterKey, id) =>
    set((state) => {
      const viewFilters = state.filters[viewName] ?? {};
      return {
        filters: {
          ...state.filters,
          [viewName]: {
            ...viewFilters,
            [filterKey]: (viewFilters[filterKey] || []).filter((item) => item.id !== id),
          },
        },
      };
    }),

  resetFilters: (viewName, filterKey) =>
    set((state) => {
      if (filterKey === undefined) {
        // Clear all filterKeys for this viewName
        return {
          filters: {
            ...state.filters,
            [viewName]: {},
          },
        };
      }
      const viewFilters = state.filters[viewName] ?? {};
      return {
        filters: {
          ...state.filters,
          [viewName]: {
            ...viewFilters,
            [filterKey]: [],
          },
        },
      };
    }),

  resetAllFilters: () => set({ filters: {} }),

  getValues: (viewName, filterKey) => get().filters[viewName]?.[filterKey] ?? [],
}));

export const useTableFilterStore = (viewName: string, filterKey: string) => {
  const addFilter = useTableFilterBaseStore((state) => state.addFilter);
  const removeFilter = useTableFilterBaseStore((state) => state.removeFilter);
  const resetFilters = useTableFilterBaseStore((state) => state.resetFilters);

  // Use the stable fallback constant — NOT an inline `?? {}` — so the selector
  // always returns the same reference when this viewName has no data yet.
  const viewFilters = useTableFilterBaseStore(
    (state) => state.filters[viewName] ?? EMPTY_VIEW_FILTERS,
  );

  const values =
    filterKey === "all"
      ? Object.values(viewFilters).flat()
      : (viewFilters[filterKey] ?? EMPTY_FILTER_VALUES);

  return {
    values,
    filters: viewFilters,
    addFilter: (filterValue: FilterValue) => addFilter(viewName, filterKey, filterValue),
    removeFilter: (id: string) => removeFilter(viewName, filterKey, id),
    resetFilters: () => resetFilters(viewName, filterKey === "all" ? undefined : filterKey),
  };
};
